import React, { useState, useEffect } from 'react';
import BRAND from '../brand';
import { SectionLoader } from '../components/NavIcons';
import { ORDER_STATUS_LABELS } from '../orderStatusLabels';
import { trackingSteps, stepIndexOf } from '../trackingSteps';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Шаги трекинга — trackingSteps.js: packaging покупателю не показываем, у самовывоза нет шага
// «У курьера». Подписи — orderStatusLabels.js.
// Раньше шага ready не было, и собранный заказ показывался на первом шаге.
function TrackOrderPage({ token }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // noindex — tracking_token не должен попадать в поисковый индекс
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    fetch(`${API_URL}/orders/track/${token}`)
      .then(res => {
        if (!res.ok) throw new Error('Заказ не найден');
        return res.json();
      })
      .then(data => {
        setOrder(data);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [token]);

  const deliveryType = order?.delivery_type;
  const getStatusIndex = (status) => stepIndexOf(status, deliveryType);
  const STATUS_STEPS = trackingSteps(deliveryType).map((key) => ({ key, label: ORDER_STATUS_LABELS[key] }));

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <SectionLoader />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', padding: '20px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>:(</div>
        <div style={{ fontSize: '16px', color: '#666', textAlign: 'center' }}>{error || 'Заказ не найден'}</div>
      </div>
    );
  }

  const currentStep = getStatusIndex(order.status);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    // "YYYY-MM-DD" парсим как локальную дату (иначе UTC → МСК даёт день-1)
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
    const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(dateStr);
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      {/* Header */}
      <div style={{
        background: 'var(--primary-color)',
        padding: '20px',
        textAlign: 'center',
      }}>
        <div style={{
          fontFamily: "'Montserrat', sans-serif",
          fontWeight: 800,
          fontSize: '24px',
          letterSpacing: '2px',
          color: '#1A1A1A',
        }}>{BRAND.name}</div>
        <div style={{ fontSize: '13px', color: '#555', marginTop: '4px' }}>
          Трекинг заказа
        </div>
      </div>

      <div style={{ padding: '20px', maxWidth: '500px', margin: '0 auto' }}>
        {/* Order number */}
        <div style={{
          fontSize: '20px',
          fontWeight: '700',
          marginBottom: '20px',
          textAlign: 'center',
        }}>
          Заказ #{order.order_number || order.id}
        </div>

        {/* Progress bar */}
        <div style={{ marginBottom: '28px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
            {/* Background line */}
            <div style={{
              position: 'absolute',
              top: '14px',
              left: '20px',
              right: '20px',
              height: '3px',
              background: '#EBEBEB',
              zIndex: 0,
            }} />
            {/* Active line */}
            <div style={{
              position: 'absolute',
              top: '14px',
              left: '20px',
              width: `${currentStep / (STATUS_STEPS.length - 1) * (100 - 10)}%`,
              height: '3px',
              background: '#4CAF50',
              zIndex: 1,
              transition: 'width 0.5s ease',
            }} />
            {STATUS_STEPS.map((step, i) => (
              <div key={step.key} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                zIndex: 2,
                flex: 1,
              }}>
                <div style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: i <= currentStep ? '#4CAF50' : '#EBEBEB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '8px',
                  transition: 'background 0.3s ease',
                }}>
                  {i <= currentStep && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
                <div style={{
                  fontSize: '11px',
                  color: i <= currentStep ? '#333' : '#999',
                  fontWeight: i === currentStep ? '600' : '400',
                  textAlign: 'center',
                }}>
                  {step.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Delivery date */}
        {order.delivery_date && (
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{deliveryType === 'pickup' ? 'Дата самовывоза' : 'Дата доставки'}</span>
            <span style={{ fontSize: '14px', fontWeight: '600' }}>
              {formatDate(order.delivery_date)}
              {order.delivery_time ? `, ${order.delivery_time}` : ''}
            </span>
          </div>
        )}

        {/* Order items */}
        {order.items && order.items.length > 0 && (
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '12px',
          }}>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '10px' }}>Состав заказа</div>
            {order.items.map((item, i) => (
              <div key={i} style={{
                display: 'flex',
                justifyContent: 'space-between',
                padding: '6px 0',
                borderBottom: i < order.items.length - 1 ? '1px solid var(--border-color)' : 'none',
              }}>
                <span style={{ fontSize: '14px' }}>
                  {item.product_name || item.name} x{item.quantity}
                </span>
                <span style={{ fontSize: '14px', fontWeight: '500' }}>
                  {((item.price || 0) * item.quantity).toLocaleString('ru-RU')} &#8381;
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Total */}
        {order.total != null && (
          <div style={{
            background: 'var(--card-bg)',
            borderRadius: '12px',
            padding: '14px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{ fontSize: '16px', fontWeight: '600' }}>Итого</span>
            <span style={{ fontSize: '18px', fontWeight: '700' }}>
              {order.total.toLocaleString('ru-RU')} &#8381;
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default TrackOrderPage;
